import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/AuthLayout";
import GoogleButton from "@/components/GoogleButton";
import { Loader2 } from "lucide-react";

export default function Register() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data);
      navigate("/profile");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle="Find jobs, internships and AI projects matched to your skills."
      footer={<>Already have an account? <Link to="/login" className="text-[#7A222B] font-semibold hover:underline" data-testid="go-login">Sign in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <div data-testid="register-error" className="bg-[#FAF0F1] border border-[#D99B9F] text-[#7A222B] text-sm rounded-xl px-4 py-3">{error}</div>}
        <div>
          <label className="text-sm font-medium text-[#2D2825]">Full name</label>
          <input
            type="text" required value={name} onChange={(e) => setName(e.target.value)}
            data-testid="register-name"
            className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition"
            placeholder="Aarav Sharma"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2D2825]">Email</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="register-email"
            className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition"
            placeholder="you@college.edu"
          />
        </div>
        <div>
          <label className="text-sm font-medium text-[#2D2825]">Password</label>
          <input
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="register-password"
            className="mt-1.5 w-full rounded-xl border border-[#EAE3DB] bg-white px-4 py-3 text-[#2D2825] focus:outline-none focus:border-[#7A222B] focus:ring-2 focus:ring-[#7A222B]/10 transition"
            placeholder="At least 6 characters"
          />
        </div>
        <button
          type="submit" disabled={loading} data-testid="register-submit-btn"
          className="w-full flex items-center justify-center gap-2 bg-[#7A222B] hover:bg-[#5C1D24] text-white font-semibold py-3 rounded-xl transition-colors disabled:opacity-60"
        >
          {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
        </button>
      </form>
      <div className="flex items-center gap-4 my-6">
        <div className="h-px bg-[#EAE3DB] flex-1" />
        <span className="text-xs text-[#948A80]">or</span>
        <div className="h-px bg-[#EAE3DB] flex-1" />
      </div>
      <GoogleButton label="Sign up with Google" />
    </AuthLayout>
  );
}
